<template lang="pug">
  a.panel.password(href="#" @click.prevent.stop="edit('password')")
    .icon
      svg.default-icon(
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      )
        path(
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.3"
          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
        )
      edit-icon
    .data
      .label Passwort
      .password-placeholder
        nobr
          span.asterisk(v-for="i in 12")
            font-awesome-icon(icon="asterisk")

    portal(to="modals")
      Modal(v-if="modal" @dismiss="dismiss" @close="close")
        validation-observer(v-slot="{ invalid }" ref="form")
          Spinner(v-if="loading" :size="16" :thickness="3")
          .p-4.bg-gray-200.rounded-t
            .alert.warning
              p Aus Sicherheitsgründen benötigen wir Ihr aktuelles Passwort, um es zu ändern
            form.pt-6(@submit.prevent="submit")
              fieldset.form-field
                validation-provider(name="Aktuelles Passwort" rules="required" v-slot="{ errors }")
                  label.label(for="current-password") Aktuelles Passwort
                  .control.has-icons-right
                    input#current-password.input(
                      type="password"
                      name="current_password"
                      v-model="form.current_password"
                      :class="{ 'is-danger': errors && errors.length }"
                    )
                    span.icon
                      font-awesome-icon(icon="key")
                  p.help.is-danger(v-if="errors && errors.length") {{ errors[0] }}
                  p.help.has-text-grey(v-else) Pflichtfeld
              fieldset.form-field
                validation-provider(name="Neues Passwort" rules="required|zxcvbn:3" v-slot="{ errors }")
                  label.label(for="new-password") Neues Passwort
                  .control.has-icons-right
                    input#new-password.input(
                      :type="password.type"
                      name="new_password"
                      v-model="form.new_password"
                      :class="{ 'is-danger': errors && errors.length }"
                    )
                    button.icon(
                      v-if="form.new_password && password.type === 'password'"
                      type="button" @click="password.type = 'text'"
                    )
                      font-awesome-icon(icon="eye")
                    button.icon(
                      v-else-if="form.new_password && password.type === 'text'"
                      type="button" @click="password.type = 'password'"
                    )
                      font-awesome-icon(icon="eye-slash")
                    span.icon(v-else)
                      font-awesome-icon(icon="key")
                  PasswordStrengthMeter(
                    :password="form.new_password"
                    @score="score => password.score = score"
                    @feedback="passwordFeedback"
                  )
                  p.help.is-danger(v-if="password.warning || (errors && errors.length)") {{ password.warning || errors[0] }}
                  p.help.has-text-grey(v-else) Pflichtfeld
                input#password-score(type="hidden" name="password-score" v-model.number="password.score")
          .bg-gray-100.p-4.rounded-b(class="md:flex md:flex-row-reverse md:justify-between")
            button.button(type="button" @click="submit" :disabled="invalid") Neues Passwort speichern
            button.button.is-outlined(type="button" @click="close") Abbrechen
</template>

<script>
import Vue from 'vue';
import uniqid from 'uniqid';

import { mapGetters, mapActions } from 'vuex';
import { get } from '@/helpers';

import { VueReCaptcha } from 'vue-recaptcha-v3';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

import PasswordStrengthMeter from '@/components/PasswordStrengthMeter';

import EditIcon from '@/extensions/restricted-area/components/UserForm/Partials/EditIcon';
import Modal from '@/components/Modal';

import { translateWarnings } from '@/extensions/restricted-area/helpers/validate';

export default {
    name: 'AccountPassword',
    data: () => ({
        modal: false,
        loading: false,
        form: {
            current_password: '',
            new_password: ''
        },
        password: {
            type: 'password',
            score: 0,
            warning: false,
            success: false,
            suggestions: []
        }
    }),
    components: {
        EditIcon,
        Modal,
        PasswordStrengthMeter,
        ValidationObserver,
        ValidationProvider
    },
    computed: {
        ...mapGetters({
            member: 'auth/member',
            recaptchaSiteKey: 'googleRecaptchaSiteKey'
        })
    },
    methods: {
        edit() {
            this.modal = true;
        },
        dismiss() {
            // if (this.form.current_password === '' && this.form.new_password === '') {
            //   this.close();
            // }
        },
        close() {
            this.modal = false;
            this.$nextTick(() => {
                this.form = {
                    current_password: '',
                    new_password: ''
                };
                this.password = {
                    type: 'password',
                    score: 0,
                    warning: false,
                    success: false,
                    suggestions: []
                };
            });
        },
        async submit() {
            this.loading = true;

            // TODO
            // 1. check the current password
            // 2. if succeed: submit member form with new password

            const valid = await this.$refs.form.validate();
            if (!valid) {
                const errorMessage = {
                    id: uniqid(),
                    title: 'Ungültiges Formular',
                    text: 'Korrigieren Sie Ihre Angaben.',
                    type: 'warning',
                    autoclose: 4000
                };
                this.addMessage(errorMessage);
            } else {
                let errorMessage = {
                    id: uniqid(),
                    title: 'Fehler',
                    text:
                        'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                    type: 'error',
                    autoclose: 8000
                };

                if (this.recaptchaSiteKey) {
                    await this.$recaptchaLoaded();
                    try {
                        await this.$recaptcha('auth');

                        // authenticate with the current password
                        // and the email from member state

                        const payload = {
                            email: this.member.email,
                            password: this.form.current_password,
                            skipRouterMapping: true
                        };

                        this.signin(payload)
                            .then(() => {
                                // submit the new password
                                // with the corrensponding id from member state
                                const payload = {
                                    id: parseInt(this.member.id),
                                    password: this.form.new_password,
                                    changePassword: true
                                };
                                this.updateMember(payload)
                                    .then(() => {
                                        this.addMessage({
                                            id: uniqid(),
                                            title: 'Erfolg',
                                            text: 'Ihr Passwort wurde erfolgreich geändert.',
                                            type: 'success',
                                            autoclose: 4000
                                        });
                                        this.close();
                                    })
                                    .catch(error => {
                                        console.error(error);
                                        this.addMessage(errorMessage);
                                    })
                                    .finally(() => {
                                        this.loading = false;
                                    });
                            })
                            .catch(error => {
                                const message = get(error, 'response.data.debugMessage');
                                if (message === 'email not verified') {
                                    errorMessage.text =
                                        'Ihre E-Mail wurde noch nicht verifiziert.<br>Bitte prüfen Sie Ihren Posteingang und bestätigen Sie Ihre E-Mail Adresse.';
                                    errorMessage.autoclose = false;
                                } else if (message === 'credentials invalid') {
                                    errorMessage.text = 'Das aktuelle Passwort ist nicht korrekt.';
                                    // errorMessage.autoclose = false;
                                    // this.form.current_password = '';
                                }
                                this.addMessage(errorMessage);
                                this.loading = false;
                            });
                    } catch (error) {
                        console.error(error);
                        this.loading = false;
                        this.addMessage(errorMessage);
                    }
                } else {
                    console.error('No ReCaptcha key!');
                    this.loading = false;
                    this.addMessage(errorMessage);
                }
            }
        },

        passwordFeedback(feedback) {
            // translateSuggestion
            this.password.warning = translateWarnings(feedback.warning);
        },
        ...mapActions({
            addMessage: 'message/add',
            signin: 'auth/signin',
            updateMember: 'auth/updateMember'
        })
    },
    beforeMount() {
        if (this.recaptchaSiteKey) {
            Vue.use(VueReCaptcha, {
                siteKey: this.recaptchaSiteKey,
                loaderOptions: {
                    useRecaptchaNet: true
                }
            });
        }
    }
};
</script>

<style lang="sass" scoped>
.spinner-container
  position: absolute
  left: 0
  width: 100%
  top: 0
  height: 100%
  z-index: 1
  background: rgba(255, 255, 255, 0.5)
form
  fieldset.form-field:last-child
    @apply mb-0
  .Password
    height: 5px
    max-width: 100%
    ::v-deep
      .Password__strength-meter
        margin-top: 5px
        margin-bottom: 0
</style>
